import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Store,
  Presentation,
  Video,
  Building2,
  Layers,
  Sparkles,
  Ruler,
  Eye,
  Palette,
  Wrench,
  Star,
  MapPin,
  Music,
} from 'lucide-react';
import './LedVisionSub.css';

const HERO_IMG =
  'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/5gntxk63_HDN-HDNPlus_%E5%AE%A4%E5%86%85_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';
const IMG_HDN = HERO_IMG;

const IndoorLedVision = () => {
  const useCases = [
    { Icon: Store, label: '店舗・ショールーム' },
    { Icon: Presentation, label: '会議室・受付' },
    { Icon: Sparkles, label: '展示ブース' },
    { Icon: Building2, label: '商業施設' },
    { Icon: Video, label: '近距離での高精細表示' },
    { Icon: Layers, label: 'イベント・レンタル設営' },
  ];

  const series = [
    {
      name: 'HDN / HDN-Plus',
      title: 'HDN / HDN-Plus（室内）',
      tagline: 'イベント・展示会・レンタル向けLEDビジョン',
      desc: 'イベントや展示会、レンタル用途に最適化した屋内用LEDビジョンです。軽量で設置性に優れたキャビネット構造により、短期間の設営から撤去までを効率化。高精細な映像表現と柔軟な設置対応で、あらゆるシーンに美しい映像体験を提供します。',
      image: IMG_HDN,
      hasImage: true,
      uses: ['イベント会場', '展示会・商業空間', 'レンタル設営', 'ステージ演出'],
      features: ['軽量アルミダイキャスト', '高精細表示', '曲面設置対応', '前後メンテナンス対応', '省施工設計'],
      specs: [
        ['主な用途', 'イベント会場 / 展示会・商業空間 / レンタル設営 / ステージ演出'],
        ['ピクセルピッチ', '1.9 / 2.6 / 2.9 / 3.9 mm'],
        ['キャビネットサイズ', '500 × 500 mm / 500 × 1000 mm'],
        ['特長', '軽量アルミダイキャスト設計 / 高精細表示 / 曲面設置対応 / 前後メンテナンス対応 / 省施工設計'],
      ],
    },
    {
      name: 'HPL',
      title: 'HPL シリーズ',
      tagline: '屋内イベント・レンタル向け高性能LEDビジョン',
      desc: '屋内イベントや展示会、ステージ演出に最適な高精細LEDビジョンです。軽量・薄型のキャビネット設計により、設営や移動がしやすく、曲線・角度設置にも柔軟に対応。美しい映像表現で、空間演出をサポートします。',
      hasImage: false,
      Icon: Video,
      uses: ['屋内イベント・展示会', 'ステージ・コンサート・式典', '企業プロモーション・デジタルサイネージ', 'レンタル・映像演出'],
      features: ['高精細で滑らかな映像表現', '軽量・薄型で設置や移動が容易', '曲線・角度設置に対応する柔軟設計', '高リフレッシュで安定した映像表示'],
      specs: [
        ['主な用途', '屋内イベント・展示会 / ステージ・レンタル'],
        ['ピクセルピッチ', '1.25 / 1.56 / 1.95 / 2.6 / 2.97 / 3.91 mm'],
        ['キャビネットサイズ', '500 × 500 × 73 mm（W × H × D）'],
        ['特長', '高精細で滑らかな映像表現 / 軽量・薄型 / 曲線・角度設置対応 / 高リフレッシュ'],
      ],
    },
    {
      name: 'HCB',
      title: 'HCB シリーズ',
      tagline: 'イベント・コンサート・ステージ向け軽量LEDビジョン',
      desc: '屋内外を問わず、イベント・コンサート・ステージなど多様な現場で活躍する軽量LEDビジョンです。明るく美しい映像表現と、設置しやすい構造設計で、効率的な運用をサポートします。',
      hasImage: false,
      Icon: Music,
      uses: ['屋内外イベント', '展示会・見本市', 'コンサート・ステージ演出'],
      features: ['高輝度で視認性に優れた映像表示', '軽量・薄型設計で設置・撤去が容易', '屋内外対応の堅牢設計（IP65 / IP54）', 'フロントメンテナンス対応'],
      specs: [
        ['主な用途', 'イベント・コンサート・ステージ'],
        ['ピクセルピッチ', '室内: 2.6 / 2.97 / 3.91 mm ・ 屋外: 3.91 / 6.94 / 8.93 mm'],
        ['キャビネットサイズ', '500 × 500 mm / 500 × 1000 mm（共通）'],
        ['特長', '高輝度・軽量設計 / 屋内外対応 / フロントメンテナンス対応'],
      ],
    },
  ];

  const points = [
    { Icon: Ruler, title: '視認距離との相性', body: '近距離の閲覧が中心の場合は、細かいピッチのシリーズを選定します。' },
    { Icon: Eye, title: '設置環境と明るさ', body: '室内照明・自然光の入り方に合わせて、適切な輝度・映り込み対策を検討します。' },
    { Icon: Palette, title: '意匠性・空間との調和', body: '内装や什器と馴染む筐体・仕上げを選び、視覚的な統一感を保ちます。' },
    { Icon: Wrench, title: '設置方法とメンテナンス', body: '壁面掛け・埋め込み・自立設置・レンタル設営など、現場に合わせた施工方法をご提案します。' },
  ];

  return (
    <div className="lvs-page" data-testid="led-vision-indoor-page">
      <section className="lvs-hero" data-testid="indoor-hero">
        <div className="lvs-hero-bg" aria-hidden="true"><img src={HERO_IMG} alt="" /></div>
        <div className="lvs-hero-overlay" aria-hidden="true" />
        <div className="lvs-hero-inner">
          <div className="lvs-hero-content">
            <Link to="/products/led-vision" className="lvs-back" data-testid="back-to-led-vision">
              <ArrowLeft size={14} /> LEDビジョン一覧へ戻る
            </Link>
            <span className="lvs-hero-eyebrow">INDOOR LED VISION</span>
            <h1 className="lvs-hero-title">屋内LEDビジョン</h1>
            <p className="lvs-hero-sub">イベント・展示・レンタルに強い、屋内向けLEDビジョン。</p>
            <p className="lvs-hero-desc">
              店舗・ショールーム・イベント会場・レンタル設営など、屋内での映像表現・情報発信に適したLEDビジョンです。用途や設置期間、視認距離に合わせて、最適なシリーズをご提案します。
            </p>
            <div className="lvs-hero-actions">
              <Link to="/contact" className="lvs-btn lvs-btn-primary">お問い合わせ <ArrowRight size={18} /></Link>
              <a href="#series" className="lvs-btn lvs-btn-outline">取扱シリーズを見る <ArrowRight size={18} /></a>
            </div>
          </div>
        </div>
      </section>

      <section className="lvs-section">
        <div className="lvs-container">
          <div className="lvs-two-col">
            <div className="lvs-intro-text">
              <span className="lvs-eyebrow">ABOUT</span>
              <h2 className="lvs-h2">屋内LEDビジョンとは</h2>
              <p>屋内LEDビジョンは、店舗・イベント会場・展示会など屋内空間での映像表示に適したディスプレイです。細かいピクセルピッチと落ち着いた輝度で、近距離でも見やすく、長時間の視聴でも目に優しい設計です。軽量な筐体構造により、常設からレンタル設営まで柔軟に対応します。</p>
            </div>
            <div className="lvs-intro-visual" aria-hidden="true"><img src={HERO_IMG} alt="" /></div>
          </div>
        </div>
      </section>

      <section className="lvs-section lvs-section-gray">
        <div className="lvs-container">
          <div className="lvs-head">
            <span className="lvs-eyebrow">USE CASES</span>
            <h2 className="lvs-h2">主な用途</h2>
            <p className="lvs-lead">屋内空間での案内表示・映像演出・情報発信など、多様なシーンでご活用いただけます。</p>
          </div>
          <div className="lvs-use-grid">
            {useCases.map((u, i) => { const Icon = u.Icon;
              return (
                <div key={i} className="lvs-use-item" data-testid={`use-${i}`}>
                  <span className="lvs-use-icon"><Icon size={18} strokeWidth={1.7} /></span>
                  <span className="lvs-use-label">{u.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="lvs-section" id="series">
        <div className="lvs-container">
          <div className="lvs-head">
            <span className="lvs-eyebrow">PRODUCT SERIES</span>
            <h2 className="lvs-h2">取扱シリーズ</h2>
            <p className="lvs-lead">屋内用途に合わせた3つのシリーズをご用意しています。</p>
          </div>
          {series.map((s, i) => {
            const Icon = s.Icon;
            return (
              <div key={i} className="lvs-series-detail" data-testid={`series-${i}`}>
                <div className="lvs-detail-top">
                  {s.hasImage ? (
                    <div className="lvs-detail-image"><img src={s.image} alt={s.name} /></div>
                  ) : (
                    <div className="lvs-detail-image-gradient" aria-hidden="true">
                      <span className="lvs-detail-gradient-icon"><Icon size={44} strokeWidth={1.4} /></span>
                    </div>
                  )}
                  <div className="lvs-detail-body">
                    <span className="lvs-detail-name-tag">{s.name}</span>
                    <h3 className="lvs-detail-title">{s.title}</h3>
                    <p className="lvs-detail-tagline">{s.tagline}</p>
                    <p className="lvs-detail-desc">{s.desc}</p>
                  </div>
                </div>
                <div className="lvs-detail-features">
                  <div className="lvs-feature-col">
                    <div className="lvs-feature-head">
                      <span><MapPin size={16} strokeWidth={1.7} /></span>
                      用途 <small>/ Use</small>
                    </div>
                    <ul className="lvs-feature-list">
                      {s.uses.map((u, k) => <li key={k}>{u}</li>)}
                    </ul>
                  </div>
                  <div className="lvs-feature-col">
                    <div className="lvs-feature-head">
                      <span><Star size={16} strokeWidth={1.7} /></span>
                      特長 <small>/ Features</small>
                    </div>
                    <ul className="lvs-feature-list">
                      {s.features.map((f, k) => <li key={k}>{f}</li>)}
                    </ul>
                  </div>
                </div>
                <div className="lvs-spec-title">製品概要 / SPECIFICATIONS</div>
                <table className="lvs-spec-table">
                  <tbody>
                    {s.specs.map((row, k) => (
                      <tr key={k}><th>{row[0]}</th><td>{row[1]}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </section>

      <section className="lvs-section lvs-section-gray">
        <div className="lvs-container">
          <div className="lvs-head">
            <span className="lvs-eyebrow">SELECTION</span>
            <h2 className="lvs-h2">選定ポイント</h2>
            <p className="lvs-lead">現場の条件を確認したうえで、最適なシリーズと仕様をご提案します。</p>
          </div>
          <div className="lvs-points">
            {points.map((p, i) => { const Icon = p.Icon;
              return (
                <div key={i} className="lvs-point-card" data-testid={`point-${i}`}>
                  <span className="lvs-point-icon"><Icon size={20} strokeWidth={1.7} /></span>
                  <h3 className="lvs-point-title">{p.title}</h3>
                  <p className="lvs-point-body">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="lvs-cta" data-testid="cta">
        <div className="lvs-cta-inner">
          <div className="lvs-cta-text">
            <h2>屋内LEDビジョンのご相談は、株式会社エイチエスへ。</h2>
            <p>設置場所・用途・ご予算に合わせて、最適なシリーズと構成をご提案いたします。</p>
          </div>
          <div className="lvs-cta-actions">
            <Link to="/contact" className="lvs-btn lvs-btn-primary">お問い合わせ <ArrowRight size={18} /></Link>
            <Link to="/products/led-vision" className="lvs-btn lvs-btn-outline"><ArrowLeft size={16} /> LEDビジョン一覧</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndoorLedVision;
